import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcTrfV1TrfReportQueryServiceTrfReportGet } from '@interxfi/api-client/generated/hooks/ixc-trf-v1-trf-report-query-service-trf-report-get';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxcTrfV1TrfReportState } from '@interxfi/types';
import { TrfReportEventDrawer, TrfReportEventSummary } from '../../../../../../../../../../features/trf/report/trf-report-event';
import { TrfReportFixMessageTable } from '../../../../../../../../../../features/trf/report/trf-report-fix-message-table';
import { getTrfReportStateListItems } from '../../../../../../../../../../features/trf/report/trf-report-list-items';
import { getTrfReportDetailPath } from '../../../../../../../../../../features/trf/report/trf-report-routing';
import { TrfReportStatusPill } from '../../../../../../../../../../features/trf/report/trf-report-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute(
  '/_app/_clearing-core/_clearing-required/clearing/operations/_operations/trade/trf-report/_trf-report/$trfReportId',
)({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `trf-report-${params.trfReportId}`,
        label: 'TRF Report',
        to: getTrfReportDetailPath(params.trfReportId),
        suffix: (
          <CopyButton
            content={params.trfReportId}
            label="Copy TRF Report ID"
            tooltip={{ copy: params.trfReportId, copied: 'Copied TRF Report ID' }}
          />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('TRF Report'),
  component: TrfReportDetail,
});

function getListItems(data: IxcTrfV1TrfReportState | undefined) {
  const listItems = getTrfReportStateListItems(data);

  return {
    general: {
      left: [
        listItems.data.symbol,
        listItems.data.mpid,
        ...(listItems.data.errorMessage ? [listItems.data.errorMessage] : []),
      ] satisfies ReadonlyListItem[],
      right: [
        listItems.metadata.createdAt,
        listItems.metadata.updatedAt,
        listItems.trfReportId,
        listItems.executionId,
        listItems.hashedId,
        ...(listItems.data.orderId ? [listItems.data.orderId] : []),
      ] satisfies ReadonlyListItem[],
    },
  };
}

function getMenuItems(trfReportId: string): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: TrfReportEventDrawer,
        props: { request: { trfReportId } },
      },
    },
    actions: undefined,
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load TRF report', 'view this TRF report');

function TrfReportDetail() {
  const { trfReportId } = Route.useParams();
  const request = { trfReportId } as const;
  const { data, error, isPending } = useIxcTrfV1TrfReportQueryServiceTrfReportGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.trfReport), [data?.trfReport]);
  const menuItems = useMemo(() => getMenuItems(trfReportId), [trfReportId]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <TrfReportStatusPill isPending={showPending} size="large" status={data?.trfReport?.status} />
          </HorizontalStack>

          <EntityActionMenu {...menuItems} isPending={showPending} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <TrfReportEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.general.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>FIX Request</Typography.Level3>

          <TrfReportFixMessageTable message={data?.trfReport?.data?.fixRequest} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>FIX Reply</Typography.Level3>

          <TrfReportFixMessageTable message={data?.trfReport?.data?.fixReply} />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
